import { createContext, useContext, useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail, setToken, getToken } from "@/lib/api";
import { ensureUserDirectory, invalidateUserDirectory } from "@/lib/userDirectory";

const AuthContext = createContext(null);

// ── Idle auto-logout ──────────────────────────────────────────────────────
// The ERP signs the user out after 30 minutes with no interaction. Last
// activity is kept in localStorage so it is shared across tabs — working in
// any tab keeps the whole session alive.
const IDLE_LIMIT_MS = 30 * 60 * 1000;
const LAST_ACTIVITY_KEY = "radiants_last_activity";
export const IDLE_LOGOUT_FLAG = "radiants_idle_logout";

function readLastActivity() {
  try { return Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || 0; } catch { return 0; }
}
function stampActivity() {
  try { localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now())); } catch {}
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=anon, obj=auth
  const [error, setError] = useState("");
  // Seconds left before idle logout (null = no warning). Drives the
  // countdown popup shown during the final minute of inactivity.
  const [idleRemaining, setIdleRemaining] = useState(null);
  const warningRef = useRef(false);

  useEffect(() => {
    // Session left idle past the limit (e.g. tab closed overnight) — force a
    // fresh login instead of silently resuming with the stale token.
    const last = readLastActivity();
    if (getToken() && last && Date.now() - last > IDLE_LIMIT_MS) {
      try { sessionStorage.setItem(IDLE_LOGOUT_FLAG, "1"); } catch {}
      api.post("/auth/logout").catch(() => {});
      setToken("");
      setUser(false);
      return;
    }
    // Only try /me if we have a token or cookies might exist
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        stampActivity();
        // Warm the email → name directory as soon as we know the user
        // is authenticated. `ensureUserDirectory` is idempotent + swallows
        // errors, so any failure here is harmless — the ERP just keeps
        // rendering raw emails until the next successful refresh.
        ensureUserDirectory();
      })
      .catch(() => { setToken(""); setUser(false); });
  }, []);

  async function login(email, password) {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data?.access_token) setToken(data.access_token);
      stampActivity();
      setUser(data.user);
      // Fresh session — always re-fetch the directory so any user
      // rename that happened while we were signed out is reflected.
      invalidateUserDirectory();
      ensureUserDirectory();
      // Return the logged-in user so the caller (Login page) can branch on
      // role for the post-login landing route (e.g. "special" → /estimates).
      return data.user || true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  }

  async function logout() {
    try { await api.post("/auth/logout"); } catch {}
    setToken("");
    setUser(false);
  }

  // Idle watcher — active only while authenticated. Activity listeners stamp
  // localStorage (throttled to 15s); a 1s ticker checks the shared stamp.
  // Final 60s → countdown popup (passive activity is IGNORED then, the user
  // must click "Stay Signed In"); 0s → logout.
  useEffect(() => {
    if (!user) return;
    stampActivity();
    let lastStamp = Date.now();
    const onActivity = () => {
      if (warningRef.current) return; // countdown showing — require explicit click
      const now = Date.now();
      if (now - lastStamp > 15000) { lastStamp = now; stampActivity(); }
    };
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    const timer = setInterval(() => {
      const last = readLastActivity();
      if (!last) return;
      const remaining = IDLE_LIMIT_MS - (Date.now() - last);
      if (remaining <= 0) {
        warningRef.current = false;
        setIdleRemaining(null);
        try { sessionStorage.setItem(IDLE_LOGOUT_FLAG, "1"); } catch {}
        logout();
      } else if (remaining <= 60000) {
        warningRef.current = true;
        setIdleRemaining(Math.ceil(remaining / 1000));
      } else if (warningRef.current) {
        // Another tab extended the session — dismiss the warning here too.
        warningRef.current = false;
        setIdleRemaining(null);
      }
    }, 1000);
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      clearInterval(timer);
      warningRef.current = false;
      setIdleRemaining(null);
    };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  function extendSession() {
    stampActivity();
    warningRef.current = false;
    setIdleRemaining(null);
  }

  function hasPerm(module, action) {
    if (!user) return false;
    if (user.role === "admin") return true;
    const perms = user.permissions || {};
    const list = perms[module] || [];
    return list.includes(action) || list.includes("all");
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, error, hasPerm, idleRemaining, extendSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
